import { Button } from "@/components/flexkit/Button";
import { useGetWorkflow, useUpdateWorkflow } from "@/queries/automation-queries";
import { useStore } from "jotai";
import { convertFormStepsToWorkflowStep, type WorkflowEditFormStepData } from "@/components/automations/automation-edit-form-data";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import type { FC } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { DEFAULT_WORKFLOW_LIMITS, WorkflowDetailsTab, workflowDetailsUrl } from "@/utils/automations";
import { useNavigate } from "react-router-dom";
import type { PrimitiveAtom } from "jotai";

type SaveAutomationStepsButtonProps = {
    workflowId: string;
    editableStepsAtom: PrimitiveAtom<WorkflowEditFormStepData[] | null>;
};

/**
 * This button saves the changes made to automation steps.
 */
export const SaveAutomationStepsButton: FC<SaveAutomationStepsButtonProps> = ({ workflowId, editableStepsAtom }) => {
    const navigate = useNavigate();

    const { mutateAsync: updateWorkflow, isPending: isUpdatingWorkflow } = useUpdateWorkflow();
    const { data: workflow } = useGetWorkflow(workflowId);
    const store = useStore();
    const { toast } = useToast();

    const save = async () => {
        const steps = store.get(editableStepsAtom);
        if (!steps || !workflow) {
            return;
        }

        try {
            const currentLimits = workflow.spec?.action?.limits;
            await updateWorkflow({
                workflowId,
                action: new WorkflowAction({
                    limits: new WorkflowAction_Limits({
                        maxParallel: currentLimits?.maxParallel || DEFAULT_WORKFLOW_LIMITS.maxParallel,
                        maxTotal: currentLimits?.maxTotal || DEFAULT_WORKFLOW_LIMITS.maxTotal,
                    }),
                    steps: convertFormStepsToWorkflowStep(steps),
                }),
            });
            navigate(workflowDetailsUrl({ id: workflowId, tab: WorkflowDetailsTab.Definition }));
        } catch (error) {
            toast({
                title: "Failed to save changes to the automation",
                description: formatError(error),
            });
        }
    };

    return (
        <Button
            variant="primary"
            data-tracking-id="save-automation-steps-automation-edit-page"
            loading={isUpdatingWorkflow}
            onClick={save}
        >
            Save
        </Button>
    );
};
